import styled from '@emotion/styled'
import { CatalogItem } from '@pubstack/common/src/catalogItem'
import { useAtomValue } from 'jotai'
import { FunctionComponent, useState } from 'react'
import { useCatalogModules } from '~/api/catalog.api'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { CatalogStatus, CatalogWrapper } from '~/components/_styled/catalog'
import Button from '~/components/Button'
import { ModuleCard } from '~/components/catalog/ModuleCard'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { BaseInputElement } from '~/components/Input'
import { PageContent } from '~/components/PageContent'
import { RouterUnstyledLink } from '~/components/RouterUnstyledLink'
import { UnstyledSelect } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Skeleton } from '~/components/Skeleton'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { configurationState } from '~/state'

const FilterAction = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
  margin: 16px 0;
  gap: 10px;
  ${BaseInputElement} {
    width: 388px;
  }
`

const getItemLink = (item: CatalogItem): string => {
  // types are not precise enough, we are routing based on what subtype of module it is
  const type = item.moduleType
  if (type === 'Identity') {
    return `identity/${item.id}`
  }
  if (type === 'RTD') {
    return `real-time-data/${item.id}`
  }
  if (type === 'Analytics') {
    return `analytics/${item.id}`
  }
  if (type === 'Rendering') {
    return `rendering/${item.id}`
  }
  throw new Error(`Missing route for this catalog item: ${item.id}`)
}

const STATUS_OPTIONS: SelectOptionProp[] = [
  {
    label: 'All status',
    value: 'all',
    trackingLabel: 'adm-integration-modules-status-filter-restore',
  },
  {
    label: 'Active',
    value: 'active',
    trackingLabel: 'adm-integration-modules-status-active-apply',
  },
  {
    label: 'Not used',
    value: 'inactive',
    trackingLabel: 'adm-integration-modules-status-inactive-apply',
  },
]

const TYPE_OPTIONS: SelectOptionProp[] = [
  {
    label: 'All types',
    value: 'all',
    trackingLabel: 'adm-integration-modules-type-filter-restore',
  },
  {
    label: 'RTD',
    value: 'RTD',
    trackingLabel: 'adm-integration-modules-type-RTD-apply',
  },
  {
    label: 'Identity',
    value: 'Identity',
    trackingLabel: 'adm-integration-modules-type-identity-apply',
  },
  {
    label: 'Analytics',
    value: 'Analytics',
    trackingLabel: 'adm-integration-modules-type-analytics-apply',
  },
]

export const ModulesPage: FunctionComponent = () => {
  // Context

  // State
  const [search, setSearch] = useState('')

  // Hooks
  const breadcrumbs = useBreadcrumbs()
  const { canEdit } = useScopePermissions()
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)

  // API Hooks
  const { data: modules, isLoading } = useCatalogModules()
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const searchedModules = modules.filter((module) => module.displayName.toLowerCase().includes(search.toLowerCase()))
  const filteredModulesByStatus = searchedModules.filter((module) => (statusFilter === 'all' ? true : module.isUsed === (statusFilter === 'active')))
  const filteredModules = filteredModulesByStatus.filter((module) => (typeFilter === 'all' ? true : module.moduleType === typeFilter))

  return (
    <PageContent breadcrumbs={breadcrumbs}>
      <FilterAction>
        <BaseInputElement type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} value={search} onChange={(event) => setSearch(event.target.value)} />
        <UnstyledSelect label={'Status'} options={STATUS_OPTIONS} value={statusFilter} onChange={(option) => setStatusFilter(option.value as string)} />
        <UnstyledSelect label={'Type'} options={TYPE_OPTIONS} value={typeFilter} onChange={(option) => setTypeFilter(option.value as string)} />

        <Button
          iconName={'close'}
          variant={'tertiary'}
          onClick={() => {
            setSearch('')
            setStatusFilter('all')
          }}
          trackingLabel={'adm-integrations-modules-filter-restore'}
        />
      </FilterAction>
      {!isLoading ? (
        <CatalogWrapper>
          {filteredModules.length > 0 ? (
            filteredModules.map((module) => (
              <RouterUnstyledLink key={module.id} to={`/monetization/one/integrations/modules/${getItemLink(module) ?? ''}`} data-tracking={`adm-integrations-modules-${module.id}-view`}>
                <ModuleCard
                  baseCDNUrl={STATIC_ASSETS_CDN}
                  code={module.code}
                  name={module.displayName}
                  description={module.tagline}
                  type={module.type}
                  moduleType={module.moduleType}
                  /* Named rather than a lone chevron: a bare ">" says the card leads somewhere but not what
                     is at the other end, and every other catalog card in the app now spells its act out
                     ("Add seat", "Upgrade"). This one opens the module's own settings, so it says so, with
                     the glyph that means settings everywhere else.

                     For a `user` it says "View" instead, and greys, because that is what the click does for
                     them: the module's page opens read-only — every toggle and field on it is an owner's.
                     The card still leads there, unlike the Catalog's "Add seat", which has nothing to show
                     a reader who cannot press it. Promising "Configure" and then disabling the whole page
                     is the one thing worth avoiding here. */
                  footerIcon={<Icon width="20px" fill={canEdit ? Colors.King : Colors.SlateGrey} name={canEdit ? 'settings' : 'view'} />}
                  actionLabel={canEdit ? 'Configure' : 'View'}
                  actionDisabled={!canEdit}
                  status={
                    <CatalogStatus state={module.isUsed ? 'active' : 'inactive'} font={Fonts.P2}>
                      {module.isUsed ? 'Active' : 'Not used'}
                    </CatalogStatus>
                  }
                />
              </RouterUnstyledLink>
            ))
          ) : (
            <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={'Oops'} message={'We can’t find what you seek. Please update your filters.'} />
          )}
        </CatalogWrapper>
      ) : (
        <CatalogWrapper>
          <Skeleton bigger width={'286px'} />
          <Skeleton bigger width={'286px'} />
          <Skeleton bigger width={'286px'} />
        </CatalogWrapper>
      )}
    </PageContent>
  )
}
